import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  DollarSign, Save, RotateCcw, Plus, Trash2, Users, Percent, Calculator,
} from 'lucide-react';

/*
 * Licensing Calculator — model what to charge other AP delivery contractors
 * (DCAs) to use the platform under licence.
 *
 * Pure client-side modelling tool. The pricing model (base per-driver rate,
 * setup fee, per-contractor platform fee, and the volume sliding scale) is
 * saved to localStorage so Paul's assumptions persist between sessions. No
 * backend — nothing here is customer-facing or billed; it's a quoting aid.
 * If it ever needs to be shared across devices, lift MODEL into a Supabase
 * settings KV row (same shape) — the maths below is storage-agnostic.
 */

const LS_KEY = 'hp_licensing_model_v1';

// Defaults reflect the recommended offer: ~AU$50/driver/mo + ~AU$8k setup,
// with a per-contractor platform fee and a volume discount that makes each
// extra contractor cheaper per head.
const DEFAULT_MODEL = {
  currency: 'AU$',
  basePerDriver: 50,        // $/driver/month at list price (before volume discount)
  platformFeePerContractor: 300, // fixed $/contractor/month (covers their dedicated instance, support)
  setupFeePerContractor: 8000,   // one-off $/contractor
  includePlatformFee: true, // toggle: charge the fixed per-contractor fee at all
  includeSetupFee: true,    // toggle: charge the one-off setup fee at all
  avgDriversPerContractor: 25,
  // Sliding scale: the more contractors licensed, the cheaper per driver.
  // Each tier gives a % discount off basePerDriver once you reach `minContractors`.
  volumeTiers: [
    { minContractors: 1,  discountPct: 0 },
    { minContractors: 3,  discountPct: 10 },
    { minContractors: 6,  discountPct: 20 },
    { minContractors: 11, discountPct: 30 },
    { minContractors: 21, discountPct: 40 },
  ],
};

function loadModel() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return DEFAULT_MODEL;
    const parsed = JSON.parse(raw);
    return { ...DEFAULT_MODEL, ...parsed, volumeTiers: parsed.volumeTiers?.length ? parsed.volumeTiers : DEFAULT_MODEL.volumeTiers };
  } catch {
    return DEFAULT_MODEL;
  }
}

// Given the model + a contractor count, resolve the effective per-driver rate
// (list price minus the best-qualifying volume discount).
function effectiveDiscount(model, contractors) {
  const tiers = [...model.volumeTiers].sort((a, b) => a.minContractors - b.minContractors);
  let pct = 0;
  for (const t of tiers) {
    if (contractors >= t.minContractors) pct = t.discountPct;
  }
  return pct;
}

const fmtMoney = (cur, n) =>
  `${cur}${Math.round(n).toLocaleString('en-AU')}`;

export default function LicensingPage() {
  const [model, setModel] = useState(loadModel);
  const [contractors, setContractors] = useState(5);
  const [saved, setSaved] = useState(false);

  // Keep the "what-if" contractor count within a sane range.
  const nContractors = Math.max(1, Math.min(200, Number(contractors) || 1));

  const persist = useCallback((next) => {
    setModel(next);
    try { localStorage.setItem(LS_KEY, JSON.stringify(next)); } catch { /* ignore */ }
    setSaved(true);
  }, []);

  useEffect(() => {
    if (!saved) return;
    const t = setTimeout(() => setSaved(false), 1500);
    return () => clearTimeout(t);
  }, [saved]);

  const set = (patch) => persist({ ...model, ...patch });
  const setTier = (i, patch) => {
    const volumeTiers = model.volumeTiers.map((t, idx) => (idx === i ? { ...t, ...patch } : t));
    persist({ ...model, volumeTiers });
  };
  const addTier = () => {
    const last = model.volumeTiers[model.volumeTiers.length - 1] || { minContractors: 1, discountPct: 0 };
    persist({ ...model, volumeTiers: [...model.volumeTiers, { minContractors: last.minContractors + 5, discountPct: Math.min(60, last.discountPct + 10) }] });
  };
  const removeTier = (i) => persist({ ...model, volumeTiers: model.volumeTiers.filter((_, idx) => idx !== i) });
  const reset = () => persist(DEFAULT_MODEL);

  const cur = model.currency;

  // ── Live quote for the selected contractor count ────────────────────────────
  const quote = useMemo(() => {
    const discPct = effectiveDiscount(model, nContractors);
    const perDriver = model.basePerDriver * (1 - discPct / 100);
    const drivers = model.avgDriversPerContractor * nContractors;

    const driverRevPerMo = perDriver * drivers;
    const platformRevPerMo = model.includePlatformFee ? model.platformFeePerContractor * nContractors : 0;
    const monthly = driverRevPerMo + platformRevPerMo;
    const annual = monthly * 12;
    const setupOneOff = model.includeSetupFee ? model.setupFeePerContractor * nContractors : 0;
    const firstYear = annual + setupOneOff;

    // Per-contractor economics (what one client pays)
    const perContractorMonthly = monthly / nContractors;
    const perContractorAnnual = perContractorMonthly * 12;

    return { discPct, perDriver, drivers, driverRevPerMo, platformRevPerMo, monthly, annual, setupOneOff, firstYear, perContractorMonthly, perContractorAnnual };
  }, [model, nContractors]);

  // ── Scale table: revenue as the book of contractors grows ───────────────────
  const scaleRows = useMemo(() => {
    const counts = [1, 2, 3, 5, 10, 15, 20, 30, 50];
    return counts.map((c) => {
      const discPct = effectiveDiscount(model, c);
      const perDriver = model.basePerDriver * (1 - discPct / 100);
      const drivers = model.avgDriversPerContractor * c;
      const platform = model.includePlatformFee ? model.platformFeePerContractor * c : 0;
      const monthly = perDriver * drivers + platform;
      const perContractorMo = monthly / c;
      return { c, discPct, perDriver, monthly, annual: monthly * 12, perContractorMo };
    });
  }, [model]);

  // ── styles (mirror the rest of the portal) ──────────────────────────────────
  const card = { background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius-lg, 12px)', padding: 16, marginBottom: 14 };
  const btn = (primary) => ({ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 14px', borderRadius: 8, fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer', border: primary ? 'none' : '1px solid var(--hp-border)', background: primary ? 'var(--hp-gold)' : 'var(--hp-surface)', color: primary ? '#1a1a1a' : 'var(--hp-text)' });
  const inputStyle = { padding: '7px 9px', borderRadius: 7, border: '1px solid var(--hp-border)', background: 'var(--hp-bg)', color: 'var(--hp-text)', fontSize: '0.82rem', fontFamily: 'inherit', width: 110 };
  const label = { fontSize: '0.74rem', color: 'var(--hp-dim)', fontWeight: 600, display: 'block', marginBottom: 4 };
  const th = { textAlign: 'right', padding: '7px 10px', fontSize: '0.7rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid var(--hp-border)' };
  const td = { textAlign: 'right', padding: '7px 10px', fontSize: '0.82rem', fontFamily: 'var(--font-mono)', borderBottom: '1px solid var(--hp-border)' };

  const Toggle = ({ on, onClick, children }) => (
    <button onClick={onClick} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '7px 12px', borderRadius: 8, border: '1px solid var(--hp-border)', background: on ? 'var(--hp-gold)' : 'var(--hp-bg)', color: on ? '#1a1a1a' : 'var(--hp-dim)', fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer' }}>
      <span style={{ width: 30, height: 16, borderRadius: 999, background: on ? '#1a1a1a' : 'var(--hp-border)', position: 'relative', display: 'inline-block', transition: 'var(--transition, .15s)' }}>
        <span style={{ position: 'absolute', top: 2, left: on ? 16 : 2, width: 12, height: 12, borderRadius: '50%', background: on ? 'var(--hp-gold)' : 'var(--hp-surface)', transition: 'var(--transition, .15s)' }} />
      </span>
      {children}
    </button>
  );

  return (
    <div style={{ maxWidth: 1180, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, padding: '4px 0 14px' }}>
        <div>
          <div style={{ fontSize: '1.3rem', fontWeight: 800, color: 'var(--hp-gold)', display: 'flex', alignItems: 'center', gap: 8 }}><DollarSign size={20} /> Licensing Calculator</div>
          <div style={{ fontSize: '0.78rem', color: 'var(--hp-dim)', marginTop: 2 }}>Model what to charge other AP contractors to licence the platform. More contractors → cheaper per contractor.</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {saved && <span style={{ fontSize: '0.74rem', color: 'var(--hp-green)', display: 'inline-flex', alignItems: 'center', gap: 4 }}><Save size={13} /> Saved</span>}
          <button style={btn(false)} onClick={reset}><RotateCcw size={14} /> Reset defaults</button>
        </div>
      </div>

      {/* Price adjustments */}
      <div style={card}>
        <div style={{ fontWeight: 800, fontSize: '0.9rem', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}><Calculator size={16} color="var(--hp-gold)" /> Price adjustments</div>
        <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <span style={label}>Base rate / driver / mo</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ color: 'var(--hp-dim)' }}>{cur}</span><input type="number" min={0} step={5} value={model.basePerDriver} onChange={e => set({ basePerDriver: Number(e.target.value) })} style={inputStyle} /></div>
          </div>
          <div>
            <span style={label}>Avg drivers / contractor</span>
            <input type="number" min={1} value={model.avgDriversPerContractor} onChange={e => set({ avgDriversPerContractor: Number(e.target.value) })} style={inputStyle} />
          </div>
          <div>
            <span style={label}>Platform fee / contractor / mo</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ color: 'var(--hp-dim)' }}>{cur}</span><input type="number" min={0} step={50} value={model.platformFeePerContractor} disabled={!model.includePlatformFee} onChange={e => set({ platformFeePerContractor: Number(e.target.value) })} style={{ ...inputStyle, opacity: model.includePlatformFee ? 1 : 0.4 }} /></div>
          </div>
          <div>
            <span style={label}>Setup fee / contractor (one-off)</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ color: 'var(--hp-dim)' }}>{cur}</span><input type="number" min={0} step={500} value={model.setupFeePerContractor} disabled={!model.includeSetupFee} onChange={e => set({ setupFeePerContractor: Number(e.target.value) })} style={{ ...inputStyle, opacity: model.includeSetupFee ? 1 : 0.4 }} /></div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 14 }}>
          <Toggle on={model.includePlatformFee} onClick={() => set({ includePlatformFee: !model.includePlatformFee })}>Charge per-contractor platform fee</Toggle>
          <Toggle on={model.includeSetupFee} onClick={() => set({ includeSetupFee: !model.includeSetupFee })}>Charge one-off setup fee</Toggle>
        </div>
      </div>

      {/* Sliding scale */}
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
          <div style={{ fontWeight: 800, fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: 6 }}><Percent size={16} color="var(--hp-gold)" /> Volume sliding scale</div>
          <button style={btn(false)} onClick={addTier}><Plus size={14} /> Add tier</button>
        </div>
        <div style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', marginBottom: 10 }}>Discount off the base per-driver rate once the number of licensed contractors reaches each threshold. The highest qualifying tier applies.</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {model.volumeTiers.map((t, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span style={{ fontSize: '0.78rem', color: 'var(--hp-dim)' }}>From</span>
              <input type="number" min={1} value={t.minContractors} onChange={e => setTier(i, { minContractors: Number(e.target.value) })} style={{ ...inputStyle, width: 80 }} />
              <span style={{ fontSize: '0.78rem', color: 'var(--hp-dim)' }}>contractors →</span>
              <input type="number" min={0} max={90} value={t.discountPct} onChange={e => setTier(i, { discountPct: Number(e.target.value) })} style={{ ...inputStyle, width: 80 }} />
              <span style={{ fontSize: '0.78rem', color: 'var(--hp-dim)' }}>% off</span>
              <span style={{ fontSize: '0.78rem', fontFamily: 'var(--font-mono)', color: 'var(--hp-text)' }}>= {fmtMoney(cur, model.basePerDriver * (1 - t.discountPct / 100))}/driver/mo</span>
              {model.volumeTiers.length > 1 && <button onClick={() => removeTier(i)} style={{ ...btn(false), padding: '6px 8px' }}><Trash2 size={13} /></button>}
            </div>
          ))}
        </div>
      </div>

      {/* Live quote */}
      <div style={{ ...card, borderColor: 'var(--hp-gold)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
          <Users size={18} color="var(--hp-gold)" />
          <span style={{ fontWeight: 800, fontSize: '0.9rem' }}>If I license to</span>
          <input type="number" min={1} max={200} value={contractors} onChange={e => setContractors(e.target.value)} style={{ ...inputStyle, width: 80, fontSize: '1rem', fontWeight: 700 }} />
          <span style={{ fontWeight: 800, fontSize: '0.9rem' }}>contractors ({quote.drivers.toLocaleString('en-AU')} drivers total):</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(180px, 100%), 1fr))', gap: 12 }}>
          <Stat label="Effective rate / driver" value={`${fmtMoney(cur, quote.perDriver)}/mo`} sub={quote.discPct ? `${quote.discPct}% volume discount` : 'list price'} />
          <Stat label="Per contractor" value={`${fmtMoney(cur, quote.perContractorMonthly)}/mo`} sub={`${fmtMoney(cur, quote.perContractorAnnual)}/yr`} />
          <Stat label="Total recurring" value={`${fmtMoney(cur, quote.monthly)}/mo`} sub={`${fmtMoney(cur, quote.annual)}/yr`} highlight />
          <Stat label="One-off setup" value={fmtMoney(cur, quote.setupOneOff)} sub={model.includeSetupFee ? `${fmtMoney(cur, model.setupFeePerContractor)} × ${nContractors}` : 'off'} />
          <Stat label="First-year total" value={fmtMoney(cur, quote.firstYear)} sub="setup + 12 mo" highlight />
        </div>
      </div>

      {/* Scale table */}
      <div style={card}>
        <div style={{ fontWeight: 800, fontSize: '0.9rem', marginBottom: 10 }}>Revenue as the book grows</div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 560 }}>
            <thead>
              <tr>
                <th style={{ ...th, textAlign: 'left' }}>Contractors</th>
                <th style={th}>Discount</th>
                <th style={th}>Rate/driver</th>
                <th style={th}>Per contractor/mo</th>
                <th style={th}>Total/mo</th>
                <th style={th}>Total/yr</th>
              </tr>
            </thead>
            <tbody>
              {scaleRows.map(r => (
                <tr key={r.c} style={r.c === nContractors ? { background: 'var(--hp-gold-lt, rgba(212,175,55,0.12))' } : undefined}>
                  <td style={{ ...td, textAlign: 'left', fontWeight: 700 }}>{r.c}</td>
                  <td style={td}>{r.discPct}%</td>
                  <td style={td}>{fmtMoney(cur, r.perDriver)}</td>
                  <td style={td}>{fmtMoney(cur, r.perContractorMo)}</td>
                  <td style={{ ...td, fontWeight: 700 }}>{fmtMoney(cur, r.monthly)}</td>
                  <td style={{ ...td, color: 'var(--hp-gold)', fontWeight: 700 }}>{fmtMoney(cur, r.annual)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ fontSize: '0.7rem', color: 'var(--hp-dim)', marginTop: 8 }}>Assumes {model.avgDriversPerContractor} drivers per contractor. Modelling tool only — figures are indicative, not a customer quote or tax advice.</div>
      </div>
    </div>
  );
}

function Stat({ label, value, sub, highlight }) {
  return (
    <div style={{ background: 'var(--hp-bg)', border: '1px solid var(--hp-border)', borderRadius: 10, padding: '12px 14px' }}>
      <div style={{ fontSize: '0.7rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: '1.35rem', fontWeight: 800, fontFamily: 'var(--font-mono)', color: highlight ? 'var(--hp-gold)' : 'var(--hp-text)', marginTop: 4 }}>{value}</div>
      {sub && <div style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}
